import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import {
  AdminOrganization,
  AdminUser,
  AdminStats,
  AdminMetrics,
  AdminUpdateSubscriptionPayload,
} from '../types/admin';
import { getAuthHeaders } from '../hooks/useAuthToken';

interface AdminContextValue {
  organizations: AdminOrganization[];
  users: AdminUser[];
  stats: AdminStats | null;
  metrics: AdminMetrics | null;
  loading: boolean;
  error: string | null;
  fetchOrganizations: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchMetrics: () => Promise<void>;
  updateSubscription: (payload: AdminUpdateSubscriptionPayload) => Promise<void>;
  getOrganization: (orgId: string) => AdminOrganization | undefined;
  getUser: (userId: string) => AdminUser | undefined;
}

const AdminContext = createContext<AdminContextValue | null>(null);

interface AdminProviderProps {
  user: User;
  children: ReactNode;
}

export function AdminProvider({ user, children }: AdminProviderProps) {
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/list-organizations', { headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch organizations');
      }
      const data = await res.json();
      setOrganizations(data.organizations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/list-users', { headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch users');
      }
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/stats', { headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch stats');
      }
      const data = await res.json();
      setStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/metrics', { headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch metrics');
      }
      const data = await res.json();
      setMetrics(data.metrics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSubscription = useCallback(
    async (payload: AdminUpdateSubscriptionPayload) => {
      setLoading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/admin/update-subscription', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to update subscription');
        }
        await fetchOrganizations();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchOrganizations]
  );

  const getOrganization = useCallback(
    (orgId: string) => organizations.find((org) => org.id === orgId),
    [organizations]
  );

  const getUser = useCallback((userId: string) => users.find((u) => u.id === userId), [users]);

  const value: AdminContextValue = {
    organizations,
    users,
    stats,
    metrics,
    loading,
    error,
    fetchOrganizations,
    fetchUsers,
    fetchStats,
    fetchMetrics,
    updateSubscription,
    getOrganization,
    getUser,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin(): AdminContextValue {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
